import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Brain,
    Languages,
    Wrench,
    GaugeCircle,
    FileText,
    ListChecks,
    Waves,
    History,
    ShieldHalf,
    MailOpen,
    FlaskConical,
    Workflow,
    CloudSun
} from 'lucide-react';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';
import AiChatPanel from 'components/AiChatPanel';

import InterpreterTab from './InterpreterTab';
import POAMTab from './POAMTab';
import RiskDialTab from './RiskDialTab';
import IssueSolverTab from './IssueSolverTab';
import ReportGenTab from './ReportGenTab';
import ZeroTrustTab from './ZeroTrustTab';
import DriftDetectorTab from './DriftDetectorTab';
import WhatChangedTab from './WhatChangedTab';
import SimulatorTab from './SimulatorTab';
import ForecastTab from './ForecastTab';
import EmailGenTab from './EmailGenTab';
import CorrelationTab from './CorrelationTab';

const TABS = [
    { key: 'interpreter', label: 'Interpreter', icon: Languages, component: InterpreterTab },
    { key: 'issueSolver', label: 'Issue Solver', icon: Wrench, component: IssueSolverTab },
    { key: 'riskDial', label: 'Risk Dial', icon: GaugeCircle, component: RiskDialTab },
    { key: 'reportGen', label: 'Report Gen', icon: FileText, component: ReportGenTab },
    { key: 'poam', label: 'POA&M', icon: ListChecks, component: POAMTab },
    { key: 'driftDetector', label: 'Drift Detector', icon: Waves, component: DriftDetectorTab },
    { key: 'whatChanged', label: 'What Changed', icon: History, component: WhatChangedTab },
    { key: 'zeroTrust', label: 'Zero Trust', icon: ShieldHalf, component: ZeroTrustTab },
    { key: 'emailGen', label: 'Email Gen', icon: MailOpen, component: EmailGenTab },
    { key: 'simulator', label: 'Simulator', icon: FlaskConical, component: SimulatorTab },
    { key: 'correlation', label: 'Correlation', icon: Workflow, component: CorrelationTab },
    { key: 'forecast', label: 'Forecast', icon: CloudSun, component: ForecastTab },
];

const AIHub = () => {
    const { scanId } = useParams();
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(TABS[0].key);
    const [url, setUrl] = useState(null);
    const [scanDate, setScanDate] = useState(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(ApiNames.DASHBOARD_SCAN(scanId));
                const data = await response.json()
                const metrics = {
                    controlsAnalyzed: data.overall_metrics.total_controls_evaluated,
                    frameworks: data.framework_breakdown.length,
                    complianceRate: data.overall_metrics.compliance_rate
                }
                setDashboardData(metrics);
                setUrl(data.url_name);
                console.log(data.last_updated);
                setScanDate(new Date(data.last_updated).toLocaleDateString());
            } catch (err) {
                setError('Failed to load dashboard data.');
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, [scanId]);

    // Top stats (same logic as Dashboard.js, but simplified for placeholder)
    const stats = dashboardData || { controlsAnalyzed: 0, frameworks: 0, complianceRate: 0 };

    return (
        <>
            <div className="min-h-screen">
                {/* Animated background grid/orbs (simple Tailwind version) */}
                <div className="absolute inset-0 pointer-events-none z-0">
                    <div className="w-full h-full opacity-10 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.2)_0,transparent_70%),radial-gradient(circle_at_80%_80%,rgba(16,185,129,0.2)_0,transparent_70%)]"></div>
                </div>

                {/* Header */}
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-6">
                    <div className="flex items-center gap-4">
                        <span className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-tr from-blue-600 to-teal-400 animate-pulse">
                            <Brain className="w-12 h-12 text-white" />
                        </span>
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight mb-1">AI Hub</h1>
                            <p className="text-black-400 text-sm mb-1">GovCompliance Intelligence Center</p>
                            {url && scanId && scanDate ? (<p className="text-gray-400 text-sm mb-1">{url} | Scan ID: {scanId} | Scan Date: {scanDate}</p>) : <p>Loading...</p>}
                        </div>
                    </div>
                    {/* Stats */}
                    <div className="flex gap-6">
                        <div className="bg-base-100 rounded-xl px-6 py-4 flex flex-col items-center shadow-lg">
                            <span className="text-2xl font-bold text-blue-400">{stats.controlsAnalyzed}</span>
                            <span className="text-xs text-gray-400 mt-1">Controls Analyzed</span>
                        </div>
                        <div className="bg-base-100 rounded-xl px-6 py-4 flex flex-col items-center shadow-lg">
                            <span className="text-2xl font-bold text-teal-400">{stats.frameworks}</span>
                            <span className="text-xs text-gray-400 mt-1">Frameworks</span>
                        </div>
                        <div className="bg-base-100 rounded-xl px-6 py-4 flex flex-col items-center shadow-lg">
                            <span className="text-2xl font-bold text-green-400">{stats.complianceRate}%</span>
                            <span className="text-xs text-gray-400 mt-1">Overall Compliance Rate</span>
                        </div>
                    </div>
                </div>

                {/* Tabs Navigation */}
                <div className="w-full mb-6">
                    <div className="flex overflow-x-auto gap-3 pb-2 scrollbar-thin scrollbar-thumb-base-300 scrollbar-track-base-100 rounded-lg">
                        {TABS.map(tab => (
                            <button
                                key={tab.key}
                                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors duration-150 focus:outline-none text-sm whitespace-nowrap shadow 
                                ${activeTab === tab.key ? 'bg-gradient-to-tr from-blue-600 to-teal-400 text-white scale-105' : 'bg-base-100 text-base-content hover:bg-base-300'}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                <tab.icon className="w-5 h-5 mr-1" />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="bg-base-100 rounded-xl shadow-xl p-6 min-h-[300px] animate-fadeIn">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <span className="animate-spin text-blue-400 text-3xl mr-3"><i className="ti ti-loader"></i></span>
                            <span className="text-gray-400">Loading dashboard data...</span>
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-400 py-12">{error}</div>
                    ) : (
                        (() => {
                            const tab = TABS.find(tab => tab.key === activeTab);
                            if (!tab) return null;
                            const Component = tab.component;
                            return <Component scanId={scanId} />;
                        })()
                    )}
                </div>
            </div>
            {/* AI Chat Panel - Rendered outside main div for fixed positioning */}
            {dashboardData && (
                <>
                    {console.log('🎨 Rendering AiChatPanel with scanId:', scanId, 'url_name:', dashboardData.url_name)}
                    <AiChatPanel
                        scanId={scanId}
                        url_name={dashboardData.url_name || 'Compliance Scan'}
                    />
                </>
            )}
        </>
    );
};

export default AIHub;
